import React from 'react';
import { CheckCircle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { services, testimonials, earlyWarnings, approaches } from '../mock';
import WhatsAppButton from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Section } from '../components/ui/section';
import { useLanguage } from '../LanguageContext';
import '../styles/harvest.css';

const Home = () => {
  const { t, language } = useLanguage();
  
  const handleWhatsAppClick = () => {
    const message = language === 'ar' 
      ? encodeURIComponent('مرحباً، أود معرفة المزيد عن برامج هارفست لاستشارات التعليم لطفلي.')
      : encodeURIComponent('Hello, I would like to learn more about Harvest Education Consulting programs for my child.');
    window.open(`https://wa.me/962790057425?text=${message}`, '_blank');
  };

  const warningSignsTranslated = [
    { icon: 'MessageCircle', title: t('home.limitedSpeech') },
    { icon: 'Focus', title: t('home.troubleFocusing') },
    { icon: 'Users', title: t('home.difficultyInteracting') },
    { icon: 'AlertCircle', title: t('home.strongEmotions') },
    { icon: 'PenTool', title: t('home.strugglesReading') },
  ];

  return (
    <div>
      {/* Hero Stack - Grid Overlay */}
      <section className="relative w-full max-h-[85vh] overflow-hidden bg-[#FFFDF9]">
        <img
          src="https://customer-assets.emergentagent.com/job_harvest-special-ed/artifacts/64ques98_Gemini_Generated_Image_94head94head94he.png"
          alt="A warm moment of support at Harvest Center"
          className="w-full h-auto block"
        />
        
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white p-5 max-w-[800px] bg-[radial-gradient(circle,_rgba(0,0,0,0.4)_0%,_transparent_70%)]">
            <h1 className="text-[clamp(2.5rem,6vw,4.5rem)] mb-4 font-bold [text-shadow:0_4px_15px_rgba(0,0,0,0.5)]">
              {t('home.heroTitle')}
            </h1>
            <p className="text-[clamp(1.1rem,2vw,1.5rem)] mb-8 [text-shadow:0_2px_10px_rgba(0,0,0,0.5)]">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex gap-5 justify-center flex-wrap">
              <a 
                href="/contact#book-consultation" 
                className="bg-brand-accent text-white px-10 py-[18px] rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 hover:bg-[#0051D5]"
              >
                {t('home.bookConsultation')}
              </a>
              <button 
                onClick={handleWhatsAppClick}
                className="bg-white/90 text-brand-text px-10 py-[18px] rounded-full font-semibold text-lg backdrop-blur-[10px] transition-all duration-300 hover:bg-white hover:scale-105"
              >
                {t('home.messageWhatsApp')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Early Warning Signs Section */}
      <Section variant="beige">
        <div className="bg-gradient-to-b from-sky-50/60 to-[#FBF7F2]">
          <div className="container py-14 md:py-18">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="heading-1 mb-3">{t('home.signsTitle')}</h2>
                <p className="body-large max-w-xl text-black/70">
                  {t('home.signsSubtitle')}
                </p>

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {warningSignsTranslated.map((w, idx) => {
                    const IconComponent = LucideIcons[w.icon];
                    return (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-sky-100/70 ring-1 ring-black/5
                                   shadow-[0_12px_35px_rgba(59,130,246,0.08)] transition-all duration-200 hover:-translate-y-0.5"
                      >
                        <div className="h-10 w-10 rounded-2xl bg-sky-100/70 ring-1 ring-sky-200/60 flex items-center justify-center">
                          {IconComponent && <IconComponent size={20} className="text-sky-700" />}
                        </div>
                        <p className="body-medium text-brand-text">{w.title}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 rounded-3xl bg-white border border-brand-border shadow-soft p-6">
                  <p className="body-medium text-black/70">
                    {t('home.signsDescription')}
                  </p>
                </div>
              </div>

              <div className="relative lg:pl-6">
                <div className="absolute -inset-6 rounded-[2rem] bg-sky-200/25 blur-3xl" />
                <img
                  src="https://customer-assets.emergentagent.com/job_harvest-special-ed/artifacts/ctsg3xzo_issues.png"
                  alt="Does your child show these signs?"
                  className="relative w-full h-[420px] md:h-[520px] rounded-3xl object-cover shadow-soft"
                />
                <div className="absolute left-0 top-0 h-full w-1/3 rounded-l-3xl bg-gradient-to-r from-[#FBF7F2] to-transparent pointer-events-none" />
              </div>
            </div>

            <div className="mt-12 h-px w-full bg-gradient-to-r from-transparent via-sky-200/70 to-transparent" />
          </div>
        </div>
      </Section>

      {/* Services - Bento Grid */}
      <Section variant="white">
        <div className="container py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1100px] mx-auto">
            {services.slice(0, 6).map((service, idx) => {
              const IconComponent = service.icon ? LucideIcons[service.icon] : null;
              const isOrange = idx % 2 === 0;
              const titleKey = `services.${['speechLanguage', 'attentionFocus', 'socialSkills', 'emotionalRegulation', 'academicSkills', 'sensoryIntegration'][idx]}`;
              const descKey = `services.${['speechLanguage', 'attentionFocus', 'socialSkills', 'emotionalRegulation', 'academicSkills', 'sensoryIntegration'][idx]}Desc`;
              
              return (
                <div
                  key={service.id}
                  className={`bg-brand-bg p-10 rounded-[30px] text-center transition-transform duration-400 hover:scale-[1.02] ${
                    isOrange ? 'border-t-8 border-brand-orange' : 'border-t-8 border-brand-blue'
                  }`}
                >
                  <div className="flex justify-center mb-4">
                    {service.imageUrl ? (
                      <img src={service.imageUrl} alt={t(titleKey)} className="w-[48px] h-[48px] object-contain" />
                    ) : IconComponent ? (
                      <IconComponent size={32} className="text-brand-text" />
                    ) : null}
                  </div>
                  <h3 className="text-2xl font-medium mb-4">{t(titleKey)}</h3>
                  <p className="text-brand-secondary">{t(descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Empathy Section */}
      <section className="py-[120px] px-[10%] bg-[#FFF9F2] text-center">
        <div className="empathy-content">
          <h2 className="text-4xl md:text-[38px] text-[#D97706] mb-6 font-bold tracking-tight">
            {t('home.empathyTitle')}
          </h2>
          <p className="text-xl md:text-2xl leading-[1.8] text-[#5F4B32] max-w-[800px] mx-auto mb-10">
            {t('home.empathyText')}
          </p>
        </div>

        <div className={`italic text-2xl md:text-3xl text-[#D97706] my-20 ${language === 'ar' ? 'border-r-[5px] pr-5 text-right' : 'border-l-[5px] pl-5 text-left'} border-brand-orange max-w-[800px] mx-auto`}>
          {t('home.empathyQuote')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-[60px] max-w-[1100px] mx-auto">
          <div className="bg-white p-8 rounded-3xl shadow-[0_10px_30px_rgba(217,119,6,0.1)]">
            <h4 className="text-brand-accent font-bold mb-3 text-lg">{t('home.frustrationToFlow')}</h4>
            <p className="text-brand-secondary leading-relaxed">
              {t('home.frustrationToFlowText')}
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-3xl shadow-[0_10px_30px_rgba(217,119,6,0.1)]">
            <h4 className="text-brand-accent font-bold mb-3 text-lg">{t('home.worryToWonder')}</h4>
            <p className="text-brand-secondary leading-relaxed">
              {t('home.worryToWonderText')}
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-3xl shadow-[0_10px_30px_rgba(217,119,6,0.1)]">
            <h4 className="text-brand-accent font-bold mb-3 text-lg">{t('home.aloneToSupported')}</h4>
            <p className="text-brand-secondary leading-relaxed">
              {t('home.aloneToSupportedText')}
            </p>
          </div>
        </div>
      </section>

      {/* Harvest Philosophy Highlight */}
      <section className="bg-gradient-to-r from-brand-blue to-[#E6E6FA] py-[60px] px-5 rounded-[30px] mx-5 my-[60px] text-center">
        <h3 className="text-3xl md:text-4xl font-bold mb-4 text-brand-text">{t('home.philosophyTitle')}</h3>
        <p className="max-w-[700px] mx-auto text-lg md:text-xl text-brand-text leading-relaxed">
          {t('home.philosophyText')}
        </p>
      </section>

      {/* Amman Warmth - Family Stories */}
      <section className="py-[120px] px-[5%] bg-[#FFF9F2]">
        <div className="max-w-[1200px] mx-auto flex flex-col gap-20">
          
          {/* Story Block 1 */}
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 min-h-[400px] bg-cover bg-center rounded-[40px] shadow-[0_20px_40px_rgba(0,0,0,0.05)] w-full lg:w-1/2"
                 style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_childsupport/artifacts/wtognqod_ChatGPT%20Image%20Jan%2012%2C%202026%2C%2010_54_28%20PM.png')" }}>
            </div>
            <div className="flex-1 lg:w-1/2">
              <h3 className="text-[#E67E22] text-3xl md:text-[28px] mb-4 font-bold">
                {t('home.story1Title')}
              </h3>
              <p className="text-lg text-[#4A4A4A] italic leading-[1.8]">
                {t('home.story1Text')}
                <br /><br /><strong>{t('home.story1Author')}</strong>
              </p>
            </div>
          </div>

          {/* Story Block 2 */}
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="flex-1 min-h-[400px] bg-cover bg-center rounded-[40px] shadow-[0_20px_40px_rgba(0,0,0,0.05)] w-full lg:w-1/2"
                 style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_harvest-special-ed/artifacts/6oqgwlrf_image.png')" }}>
            </div>
            <div className="flex-1 lg:w-1/2">
              <h3 className="text-[#E67E22] text-3xl md:text-[28px] mb-4 font-bold">
                {t('home.story2Title')}
              </h3>
              <p className="text-lg text-[#4A4A4A] italic leading-[1.8]">
                {t('home.story2Text')}
                <br /><br /><strong>{t('home.story2Author')}</strong>
              </p>
            </div>
          </div>

        </div>

        {/* Community Promise */}
        <div className="bg-[#F0F7FF] py-[60px] px-[60px] rounded-[30px] text-center mt-[100px] max-w-[1000px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-5 text-[#2C3E50]">
            {t('home.promiseTitle')}
          </h2>
          <p className="text-lg text-[#555] max-w-[700px] mx-auto leading-relaxed">
            {t('home.promiseText')}
          </p>
        </div>
      </section>

      {/* Approach Section */}
      <Section variant="beige">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="heading-1 mb-3">{t('home.approachTitle')}</h2>
            <p className="body-large" style={{ maxWidth: '800px', margin: '0 auto' }}>
              {t('home.approachSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {['iep', 'aba', 'speech', 'ot', 'social'].map((key, index) => {
              const icons = ['ClipboardList', 'TrendingUp', 'MessageSquare', 'Hand', 'Smile'];
              const IconComponent = LucideIcons[icons[index]];
              return (
                <div key={index} className="bg-white rounded-lg p-5 hover-lift">
                  <div className="mb-3">
                    {IconComponent && <IconComponent size={28} className="text-gray-700" />}
                  </div>
                  <h3 className="heading-3 mb-2">{t(`approaches.${key}`)}</h3>
                  <p className="body-small">{t(`approaches.${key}Desc`)}</p>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-5 p-6 bg-white rounded-lg max-w-3xl mx-auto">
            <h3 className="heading-2 mb-3">{t('home.progressTitle')}</h3>
            <p className="body-medium mb-4">{t('home.progressSubtitle')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                <span className="body-small">{t('home.progressItem1')}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                <span className="body-small">{t('home.progressItem2')}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                <span className="body-small">{t('home.progressItem3')}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                <span className="body-small">{t('home.progressItem4')}</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Testimonials Section */}
      <Section variant="white">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="heading-1 mb-3">{t('home.testimonialsTitle')}</h2>
            <p className="body-large" style={{ maxWidth: '700px', margin: '0 auto' }}>
              {t('home.testimonialsSubtitle')}
            </p>
          </div>
          
          <div className="testimonial-grid">
            {testimonials.map((testimonial, idx) => (
              <Card key={testimonial.id}>
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-500">★</span>
                  ))}
                </div>
                <p className="body-medium mb-4 text-brand-text" style={{ fontStyle: 'italic', lineHeight: '1.7' }}>
                  "{t(`testimonials.t${idx + 1}`)}"
                </p>
                <div className="testimonial-meta">
                  <p className="testimonial-author">{testimonial.parent} — {language === 'ar' ? 'عمّان' : testimonial.location}</p>
                  <p className="testimonial-program">{testimonial.program} • {testimonial.duration}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section variant="beige">
        <div className="container">
          <div className="bg-white rounded-lg p-8 md:p-12 text-center max-w-3xl mx-auto" style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)' }}>
            <h2 className="heading-1 mb-3">{t('home.ctaTitle')}</h2>
            <p className="body-large mb-5">
              {t('home.ctaText')}
            </p>
            <div className="flex flex-wrap gap-3 justify-center items-center">
              <WhatsAppButton />
              <Button variant="secondary" onClick={() => window.location.href = '/contact#book-consultation'}>
                {t('home.scheduleFree')}
              </Button>
            </div>
            <p className="caption mt-4">
              {testimonials[0].parent.split(' ')[0]} {t('home.ctaNote')}
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
